import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';

// PNGs, not the SVGs, for the id-collision reason FriendCard gives.
import VipCrownPng from '../../../assets/profileRedesign/vip-crown.png';
import FriendIcon from '../../../assets/social/friend.svg';
import StatusDotOnlinePng from '../../../assets/social/status-dot-online.png';
import { Pressable } from '../common/Touchable';
import { SOCIAL_FONT, socialColors, socialGlow } from './socialTheme';

export type FriendsFilter = 'friends' | 'vip' | 'online';

const TABS: { key: FriendsFilter; label: string; labelKey: string }[] = [
	{ key: 'friends', label: 'Friends', labelKey: 'T_SITE_MENU_FRIENDS' },
	{ key: 'vip', label: 'VIP', labelKey: 'T_SITE_VIP' },
	{ key: 'online', label: 'Online', labelKey: 'T_SITE_FRIENDS_STATUS_ONLINE' },
];

type FriendsFilterTabsProps = {
	/** Null: no tab selected, every player is listed. */
	active: FriendsFilter | null;
	/** Tapping the selected tab again unselects it (null). */
	onChange: (filter: FriendsFilter | null) => void;
};

const TabIcon = ({ filter }: { filter: FriendsFilter }) => {
	if (filter === 'friends') return <FriendIcon width={15} height={15} />;
	if (filter === 'vip')
		return <Image source={VipCrownPng} resizeMode='contain' style={styles.crown} />;

	return (
		<View style={styles.onlineDotSlot}>
			<Image source={StatusDotOnlinePng} style={styles.onlineDotArt} />
		</View>
	);
};

const FriendsFilterTabs = ({ active, onChange }: FriendsFilterTabsProps) => {
	const { t } = useTranslation();

	return (
		<View style={styles.row}>
			{TABS.map(({ key, label, labelKey }) => {
				const isActive = key === active;

				return (
					<Pressable
						key={key}
						accessibilityRole='button'
						accessibilityState={{ selected: isActive }}
						onPress={() => onChange(isActive ? null : key)}
						style={[styles.tab, isActive ? styles.tabActive : styles.tabInactive]}
					>
						<TabIcon filter={key} />
						<Text numberOfLines={1} style={styles.label}>
							{t(labelKey, { defaultValue: label })}
						</Text>
					</Pressable>
				);
			})}
		</View>
	);
};

const styles = StyleSheet.create({
	row: {
		flexDirection: 'row',
		gap: 10,
	},
	tab: {
		flex: 1,
		height: 34,
		borderRadius: 17,
		borderWidth: 1,
		backgroundColor: socialColors.plate,
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 6,
	},
	tabActive: {
		borderColor: socialColors.borderYellow,
		...socialGlow.tabActive,
	},
	tabInactive: {
		borderColor: socialColors.borderOrange,
		...socialGlow.tabInactive,
	},
	label: {
		color: socialColors.label,
		fontFamily: SOCIAL_FONT,
		fontSize: 13,
		fontWeight: '700',
		lineHeight: 13,
		letterSpacing: 0.05,
	},
	onlineDotSlot: {
		width: 11,
		height: 11,
	},
	onlineDotArt: {
		position: 'absolute',
		top: -5.5,
		left: -5.5,
		width: 22,
		height: 22,
	},
	crown: {
		width: 17,
		height: 15,
	},
});

export default FriendsFilterTabs;
